import type {
  ProjectFileAiCost,
  ProjectFileAiStepCost,
} from '@shared/helpers/project-file-pipeline-ai.helpers';
import type { ProjectFilePipelineAiStep } from '@shared/models/ai/project-file-pipeline-ai.model';

/** Token count in thousands or millions, for example `675k` or `1.77M`. */
export const formatTokenCount = (tokens: number): string => {
  if (tokens >= 1_000_000) return `${(tokens / 1_000_000).toFixed(2)}M`;
  if (tokens >= 1_000) return `${Math.round(tokens / 1_000)}k`;
  return String(tokens);
};

/**
 * Estimated cost for the batch list, for example `≈ $3.55`. Null when no call
 * has a known price.
 */
export const formatAiCostUsd = (cost: ProjectFileAiCost): string | null =>
  cost.calls > cost.unpricedCalls ? `≈ $${cost.usd.toFixed(2)}` : null;

/**
 * Cost and token summary for the batch header, for example
 * `AI ≈ $3.55 · 49 calls · 1.77M in (675k cached) · 130k out`.
 */
export const formatAiCostSummary = (cost: ProjectFileAiCost): string | null => {
  if (cost.calls === 0) return null;
  const parts = [
    `AI ${formatAiCostUsd(cost) ?? 'price unknown'}`,
    `${cost.calls} ${cost.calls === 1 ? 'call' : 'calls'}`,
    `${formatTokenCount(cost.inputTokens)} in (${formatTokenCount(cost.cachedInputTokens)} cached)`,
    `${formatTokenCount(cost.outputTokens)} out`,
  ];
  if (cost.failedCalls > 0) parts.push(`${cost.failedCalls} failed`);
  if (cost.unpricedCalls > 0 && cost.calls > cost.unpricedCalls) {
    parts.push(`${cost.unpricedCalls} without a price`);
  }
  return parts.join(' · ');
};

const AI_STEP_LABEL: Record<ProjectFilePipelineAiStep, string> = {
  classify: 'Classification',
  drawingDetection: 'Drawing detection',
  drawingFootprint: 'Footprints',
  drawingScale: 'Scale',
  siteModel: 'Site model',
  drawingSegmentExtraction: 'Drawing reading',
  textSegmentExtraction: 'Text reading',
  joinedBlueprintExtraction: 'Whole-building reading',
  consolidation: 'Merge',
  ffuCorrection: 'Correction',
  ffuChatMessage: 'Chat message',
};

export type AiStepCostRow = {
  step: ProjectFilePipelineAiStep;
  label: string;
  calls: string;
  usd: string;
  input: string;
  cached: string;
  output: string;
  longContext: string;
};

/** One table row per pipeline step for the batch cost breakdown. */
export const aiStepCostRow = (cost: ProjectFileAiStepCost): AiStepCostRow => ({
  step: cost.step,
  label: AI_STEP_LABEL[cost.step],
  calls:
    cost.failedCalls > 0
      ? `${cost.calls} (${cost.failedCalls} failed)`
      : String(cost.calls),
  usd: formatAiCostUsd(cost) ?? 'price unknown',
  input: formatTokenCount(cost.inputTokens),
  cached: formatTokenCount(cost.cachedInputTokens),
  output: formatTokenCount(cost.outputTokens),
  longContext: cost.longContextCalls > 0 ? String(cost.longContextCalls) : '',
});
